import { API_URL } from "../config";
import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, ExternalLink, Copy, CheckCircle2, Link as LinkIcon, Globe, LayoutTemplate, Layers, List, Bot, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomDropdown from '../components/CustomDropdown';

const detectPlatform = (url) => {
  if (!url) return 'website';
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.includes('t.me') || lowerUrl.includes('telegram.me')) return 'telegram';
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.me')) return 'facebook';
  if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) return 'instagram';
  return 'website';
};

const CreateLink = ({ onSuccess, initialData = null }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [destinationUrl, setDestinationUrl] = useState(initialData?.destinationUrl || '');
  const [steps, setSteps] = useState(initialData?.steps || [{ type: 'youtube', url: '' }]);
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [tgChatId, setTgChatId] = useState('');
  const [customTgChatId, setCustomTgChatId] = useState('');
  const [tgJoinType, setTgJoinType] = useState('direct'); 
  const [generatingTgLink, setGeneratingTgLink] = useState(false);
  const [tgError, setTgError] = useState('');
  const [tgSuccess, setTgSuccess] = useState('');
  const [error, setError] = useState('');

  const [myTgChats, setMyTgChats] = React.useState([]);
  const [loadingChats, setLoadingChats] = React.useState(false);
  const isMiniApp = !!window.Telegram?.WebApp?.initData;

  React.useEffect(() => {
      if (isMiniApp) {
          setLoadingChats(true);
          axios.get(`${API_URL}/telegram/my-chats`)
               .then(res => setMyTgChats(res.data))
               .catch(err => console.error("Failed to load user chats", err))
               .finally(() => setLoadingChats(false));
      }
  }, [isMiniApp]);


  const handleAddStep = () => {
    setSteps([...steps, { type: 'youtube', url: '' }]);
  };

  const handleGenerateTgLink = async () => {
    const activeChatId = tgChatId === 'custom' ? customTgChatId : tgChatId;
    if (!activeChatId) {
        setTgError('Please enter a Channel/Group username or ID');
        setTgSuccess('');
        return;
    }
    setGeneratingTgLink(true);
    setTgError('');
    setTgSuccess('');
    try {
        const response = await axios.post(`${API_URL}/telegram/generate-link`, {
            chatId: activeChatId,
            joinType: tgJoinType
        });
        
        const newStep = { 
            type: 'telegram', 
            url: response.data.url, 
            chatId: response.data.chatId, 
            joinType: response.data.joinType,
            chatName: response.data.chatName
        };

        if (steps.length === 1 && steps[0].url === '') {
            setSteps([newStep]);
        } else {
            
            setSteps([...steps, newStep]);
        }
        
        setTgChatId(''); 
        setTgSuccess('Telegram step successfully generated and added below!');
        setTimeout(() => setTgSuccess(''), 5000);
    } catch (err) {
        setTgError(err.response?.data?.error || 'Failed to generate link. Ensure the bot is an admin in the channel.');
    } finally {
        setGeneratingTgLink(false);
    }
  };
const handleRemoveStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const handleStepChange = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index][field] = value;

    if (field === 'url') {
        newSteps[index].type = detectPlatform(value);
    }

    setSteps(newSteps);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!destinationUrl) {
      setError('Destination URL is required');
      setLoading(false);
      return;
    }
    if (steps.some(s => !s.url)) {
      setError('All step URLs must be filled');
      setLoading(false);
      return;
    }

    try {
      const url = initialData ? `${API_URL}/links/${initialData.id}` : `${API_URL}/links`;
      const method = initialData ? 'put' : 'post';
      const response = await axios[method](url, {
        title,
        destinationUrl,
        steps,
      });
      setGeneratedLink(response.data.url);
      if (typeof onSuccess === 'function') onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    alert('Copied to clipboard!');
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-500/10 text-red-400 p-4 rounded-xl border border-red-500/20 mb-6 flex items-center gap-3"
            >
              <div className="bg-red-500/20 p-2 rounded-lg">
                 <ExternalLink className="h-5 w-5 text-red-400" />
              </div>
              <span className="text-sm font-medium">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {!generatedLink ? (
          <motion.form 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
            onSubmit={handleSubmit} 
            className="space-y-8"
          >
            {}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-xl relative group">
               <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
                   <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                       <LayoutTemplate className="w-32 h-32 text-blue-500 -mt-10 -mr-10 rotate-12" />
                   </div>
               </div>
               <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3 relative z-10">
                   <div className="p-2.5 bg-blue-500/20 rounded-xl border border-blue-500/30">
                       <LinkIcon className="h-5 w-5 text-blue-400" />
                   </div>
                   Target Destination
               </h3>
               
               <div className="space-y-5 relative z-10">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                        <label className="block text-sm font-bold text-slate-300 mb-2">Link Title <span className="text-slate-500 font-normal">(Optional)</span></label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="block w-full bg-black/40 border border-white/10 rounded-2xl py-3.5 px-5 text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                            placeholder="e.g., Exclusive Content Bundle"
                        />
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                        <label className="block text-sm font-bold text-slate-300 mb-2">Destination URL <span className="text-red-400">*</span></label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <Globe className="h-5 w-5 text-slate-500" />
                            </div>
                            <input
                                type="url"
                                value={destinationUrl}
                                onChange={(e) => setDestinationUrl(e.target.value)}
                                className="block w-full bg-black/40 border border-white/10 rounded-2xl py-3.5 pl-12 pr-5 text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                                placeholder="https://example.com/secret-file.zip"
                                required
                            />
                        </div>
                    </motion.div>
               </div>
            </div>

            {}
            <div className="bg-[#121a2f] rounded-3xl p-6 border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)] relative overflow-hidden mb-6">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                    <Send className="w-40 h-40 -mt-10 -mr-10 text-blue-500" />
                </div>
                <div className="relative z-10">
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                        <div className="p-2 bg-blue-500/20 rounded-lg"><Bot className="h-5 w-5 text-blue-400" /></div>
                        Telegram Force Sub
                    </h3>
                    <p className="text-sm text-slate-400 mb-4">Add our bot <b>@{process.env.REACT_APP_TELEGRAM_BOT_NAME || 'OfficialLinkeyBot'}</b> as an admin to your channel/group, then enter the ID below to auto-generate a secure join link.</p>
                    
                    <div className="flex flex-col gap-3">

                        {isMiniApp ? (
                            <div className="flex flex-col gap-3">
                                {loadingChats ? (
                                    <p className="text-slate-400 text-sm">Loading your chats...</p>
                                ) : myTgChats.length > 0 ? (
                                    <div className="flex-1">
                                        <select
                                            value={tgChatId}
                                            onChange={(e) => setTgChatId(e.target.value)}
                                            className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none"
                                        >
                                            <option value="">Select a chat...</option>
                                            {myTgChats.map(chat => (
                                                <option key={chat.id} value={chat.id}>{chat.title} {chat.username ? `(@${chat.username})` : ''}</option>
                                            ))}
                                            <option value="custom">Enter manually...</option>
                                        </select>
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 mb-2">You haven't added the bot to any of your chats yet. <a href={`https://t.me/${process.env.REACT_APP_TELEGRAM_BOT_NAME || 'OfficialLinkeyBot'}?startchannel=true&admin=invite_users`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Add Bot to Channel</a></p>
                                )}
                                {(myTgChats.length === 0 || tgChatId === 'custom') && (
                                    <div className="flex-1 mt-2">
                                        <input
                                            type="text"
                                            value={tgChatId === 'custom' ? customTgChatId : tgChatId}
                                            onChange={(e) => tgChatId === 'custom' ? setCustomTgChatId(e.target.value) : setTgChatId(e.target.value)}
                                            placeholder="@channelname or -100123456"
                                            className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={tgChatId === 'custom' ? customTgChatId : tgChatId}
                                    onChange={(e) => setTgChatId(e.target.value)}
                                    placeholder="@channelname or -100123456"
                                    className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                        )}

                        <div className="w-full sm:w-48">
                            <select 
                                value={tgJoinType}
                                onChange={(e) => setTgJoinType(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-4 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none"
                            >
                                <option value="direct">Direct Join</option>
                                <option value="request">Request to Join</option>
                            </select>
                        </div>
                        <button 
                            type="button"
                            onClick={handleGenerateTgLink}
                            disabled={generatingTgLink}
                            className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            {generatingTgLink ? 'Generating...' : 'Add Telegram Step'}
                        </button>
                    </div>
                    {tgError && <p className="text-red-400 text-sm mt-2">{tgError}</p>}
                    {tgSuccess && <p className="text-green-400 text-sm mt-2">{tgSuccess}</p>}
                </div>
            </div>
            
            {}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-xl relative group">
               <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-3xl">
                   <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                       <Layers className="w-32 h-32 text-indigo-500 -mt-10 -mr-10 -rotate-12" />
                   </div>
               </div>
               <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 relative z-10">
                   <h3 className="text-xl font-bold text-white flex items-center gap-3">
                       <div className="p-2.5 bg-indigo-500/20 rounded-xl border border-indigo-500/30">
                           <CheckCircle2 className="h-5 w-5 text-indigo-400" />
                       </div>
                       Unlock Requirements
                   </h3>
                   <button
                       type="button"
                       onClick={handleAddStep}
                       className="inline-flex items-center justify-center px-4 py-2 border border-blue-500/30 text-sm font-bold rounded-xl text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 transition-all active:scale-95 shadow-sm shadow-blue-500/5"
                   >
                       <Plus className="h-4 w-4 mr-2" /> Add Step
                   </button>
               </div>

               <div className="space-y-4 relative z-10">
                 <AnimatePresence>
                   {steps.map((step, index) => (
                     <motion.div 
                       key={index} 
                       initial={{ opacity: 0, scale: 0.95, y: 10 }}
                       animate={{ opacity: 1, scale: 1, y: 0 }}
                       exit={{ opacity: 0, scale: 0.95, height: 0 }}
                       transition={{ duration: 0.2 }}
                       className="bg-black/40 p-5 rounded-2xl border border-white/10 hover:border-white/20 transition-colors group/step"
                     >
                       <div className="flex flex-col sm:flex-row gap-4">
                           <div className="relative w-full md:w-56 shrink-0" style={{ zIndex: steps.length - index + 10 }}>
                               <CustomDropdown
                                   options={[
                                       { value: 'youtube', label: 'YouTube Subscribe' },
                                       { value: 'telegram', label: 'Telegram Join' },
                                       { value: 'facebook', label: 'Facebook Follow' },
                                       { value: 'instagram', label: 'Instagram Follow' },
                                       { value: 'website', label: 'Visit Website' }
                                   ]}
                                   value={step.type}
                                   onChange={(value) => handleStepChange(index, 'type', value)}
                                   icon={List}
                                   className="py-3 px-4 shadow-sm"
                               />
                           </div>
                           <div className="flex-1 flex gap-3">
                               <input
                                   type="url"
                                   value={step.url}
                                   onChange={(e) => handleStepChange(index, 'url', e.target.value)}
                                   className={`block w-full bg-slate-900/80 border border-slate-700/80 rounded-xl py-3 px-4 text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all hover:bg-slate-900 ${step.chatId ? 'opacity-70 cursor-not-allowed' : ''}`}
                                   placeholder={`Enter ${step.type === 'website' ? 'website' : step.type} URL...`}
                                   readOnly={!!step.chatId}
                                   required
                               />
                               {steps.length > 1 && (
                                   <button
                                       type="button"
                                       onClick={() => handleRemoveStep(index)}
                                       className="shrink-0 p-3 text-slate-400 hover:text-red-400 bg-slate-900/80 hover:bg-red-500/10 rounded-xl transition-colors border border-slate-700/80 hover:border-red-500/30"
                                       title="Remove Step"
                                   >
                                       <Trash2 className="h-5 w-5" />
                                   </button>
                               )}
                           </div>
                       </div>
                     </motion.div>
                   ))}
                 </AnimatePresence>
               </div>
            </div>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-5 px-6 rounded-2xl text-base font-black tracking-wide text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:via-indigo-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-black transition-all shadow-[0_0_30px_rgba(79,70,229,0.3)] hover:shadow-[0_0_40px_rgba(79,70,229,0.5)] disabled:opacity-50 disabled:shadow-none uppercase"
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                  Generating Magic Link...
                </div>
              ) : 'Create Gateway Link'}
            </motion.button>
          </motion.form>
        ) : (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center space-y-8 py-10 px-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl relative overflow-hidden"
          >
            {}
            <div className="absolute inset-0 bg-gradient-to-b from-green-500/10 to-transparent pointer-events-none"></div>

            <motion.div 
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="bg-gradient-to-br from-green-400 to-emerald-600 p-1 rounded-full w-28 h-28 mx-auto flex items-center justify-center shadow-[0_0_50px_rgba(16,185,129,0.4)] relative z-10"
            >
              <div className="bg-black/50 rounded-full w-full h-full flex items-center justify-center backdrop-blur-sm">
                  <CheckCircle2 className="h-14 w-14 text-white drop-shadow-md" />
              </div>
            </motion.div>
            
            <div className="relative z-10">
                <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 mb-3">Gateway Created!</h3>
                <p className="text-slate-400 text-base max-w-md mx-auto">Your locked link is ready to share. Anyone who visits will need to complete your steps to unlock the destination.</p>
            </div>

            <div className="bg-black/60 p-3 rounded-2xl flex items-center gap-3 border border-white/10 max-w-lg mx-auto relative z-10 shadow-inner">
              <div className="p-2 bg-blue-500/20 rounded-xl">
                 <LinkIcon className="h-5 w-5 text-blue-400" />
              </div>
              <input
                type="text"
                readOnly
                value={generatedLink}
                className="block w-full bg-transparent border-none text-white font-mono focus:ring-0 text-sm p-0 selection:bg-blue-500/30"
              />
              <button
                onClick={copyToClipboard}
                className="p-3 text-white hover:text-white bg-blue-600 hover:bg-blue-500 rounded-xl transition-all shadow-md active:scale-95 flex items-center gap-2 font-bold text-sm"
              >
                <Copy className="h-4 w-4" /> <span className="hidden sm:inline">Copy</span>
              </button>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4 max-w-lg mx-auto relative z-10">
              <a
                href={generatedLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex justify-center items-center px-6 py-4 border border-white/10 text-base font-bold rounded-2xl text-white bg-white/5 hover:bg-white/10 transition-colors shadow-lg"
              >
                Test Gateway <ExternalLink className="ml-2 h-5 w-5 text-slate-400" />
              </a>
              <button
                onClick={() => {
                  setGeneratedLink('');
                  setTitle('');
                  setDestinationUrl('');
                  setSteps([{ type: 'youtube', url: '' }]);
                  if (typeof onSuccess === 'function') onSuccess();
                }}
                className="flex-1 flex justify-center items-center px-6 py-4 border border-transparent text-base font-bold rounded-2xl text-white bg-slate-800 hover:bg-slate-700 transition-colors shadow-lg"
              >
                Create Another
              </button>
            </div>
          </motion.div>
        )}
    </motion.div>
  );
};

export default CreateLink;